/**
 * Budgets Dashboard
 *
 * Role-branched:
 *  - Admin / BudgetAdmin — the organization-wide overview (stat cards,
 *    consumption by type, top consumers, at-risk, recent alerts) from
 *    GET api/budget-dashboard/overview, plus quick links to the v2
 *    admin surfaces (Analytics, Combined view, Change requests, AI
 *    insights).
 *  - BudgetManager (non-admin) — "My Budgets" from
 *    GET api/budget-dashboard/my-overview: totals, per-budget cards
 *    with consumed + released-vs-spent bars, health, alerts and the
 *    caller's open change requests.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconWallet,
  IconLockDollar,
  IconReceipt2,
  IconCoins,
  IconReceipt,
  IconPlus,
  IconArrowRight,
  IconRefresh,
  IconBell,
  IconAlertTriangle,
  IconChartBar,
  IconTableOptions,
  IconGitPullRequest,
  IconSparkles,
  IconArrowUpRight,
  IconArrowDownRight,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Grid,
  Group,
  Badge,
  Button,
  Text,
  Title,
  ThemeIcon,
  Skeleton,
  SimpleGrid,
  Paper,
  Progress,
  Tooltip,
} from '@/components/ui';
import { UnstyledButton } from '@mantine/core';
import { FadeIn } from '@/components/animations';
import { useBudgetDashboardService, type BudgetMyOverviewRow } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { BudgetHealthBadge, BudgetStatusBadge, BudgetTypeBadge, PageGuide } from '../components';
import { formatMoney, formatStampDateTime, consumptionColor, consumptionGradient } from '../components/budgetDates';

const ADMIN_TIER_ROLES = ['Admin', 'BudgetAdmin'];

export default function BudgetsDashboard() {
  const { hasAnyRole } = useEnrichedAuth();
  const isAdminTier = hasAnyRole(ADMIN_TIER_ROLES);
  return isAdminTier ? <AdminOverviewDashboard /> : <MyBudgetsDashboard />;
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

function DashboardSkeleton() {
  return (
    <FadeIn>
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <SimpleGrid cols={{ base: 2, md: 5 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={120} />
          ))}
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Skeleton height={300} />
          <Skeleton height={300} />
        </SimpleGrid>
      </Stack>
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/* Admin / BudgetAdmin — org overview + v2 quick links                 */
/* ------------------------------------------------------------------ */

function AdminQuickLinks() {
  const navigate = useNavigate();
  const links = [
    {
      label: 'Analytics',
      description: 'Spend by department, location, project or user',
      icon: <IconChartBar size={20} />,
      color: 'blue',
      path: '/app/budgets/analytics',
    },
    {
      label: 'Combined View',
      description: 'Cross-dimension spend matrix',
      icon: <IconTableOptions size={20} />,
      color: 'teal',
      path: '/app/budgets/analytics/combined',
    },
    {
      label: 'Change Requests',
      description: 'Review requested budget amount changes',
      icon: <IconGitPullRequest size={20} />,
      color: 'orange',
      path: '/app/budgets/change-requests',
    },
    {
      label: 'AI Insights',
      description: 'Spend patterns and overspend predictions',
      icon: <IconSparkles size={20} />,
      color: 'grape',
      path: '/app/budgets/ai-insights',
    },
  ];

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
      {links.map((link) => (
        <UnstyledButton key={link.path} onClick={() => navigate(link.path)}>
          <Card withBorder p="md" h="100%">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color={link.color} size="lg">
                {link.icon}
              </ThemeIcon>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Group gap={4} wrap="nowrap">
                  <Text size="sm" fw={600}>
                    {link.label}
                  </Text>
                  <IconArrowRight size={14} style={{ flexShrink: 0 }} />
                </Group>
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {link.description}
                </Text>
              </div>
            </Group>
          </Card>
        </UnstyledButton>
      ))}
    </SimpleGrid>
  );
}

function AdminOverviewDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboardService = useBudgetDashboardService();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const { data: overview, isLoading } = useQuery({
    queryKey: ['budgets', 'dashboard', 'overview'],
    queryFn: () => dashboardService.getOverview(),
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['budgets', 'dashboard'] });
    setLastRefresh(new Date());
    setIsRefreshing(false);
  };

  const formatLastRefresh = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const statCards = [
    {
      label: 'Total Allocated',
      value: formatMoney(overview?.totalAllocated),
      icon: <IconWallet size={22} />,
      color: 'blue',
      onClick: undefined as (() => void) | undefined,
    },
    {
      label: 'Committed',
      value: formatMoney(overview?.totalCommitted),
      icon: <IconLockDollar size={22} />,
      color: 'orange',
      onClick: undefined,
    },
    {
      label: 'Actual',
      value: formatMoney(overview?.totalActual),
      icon: <IconReceipt2 size={22} />,
      color: 'violet',
      onClick: undefined,
    },
    {
      label: 'Available',
      value: formatMoney(overview?.totalAvailable),
      icon: <IconCoins size={22} />,
      color: 'green',
      onClick: undefined,
    },
    {
      label: 'Unbudgeted',
      value: formatMoney(overview?.unbudgetedTotal),
      icon: <IconReceipt size={22} />,
      color: 'red',
      onClick: () => navigate('/app/budgets/unbudgeted'),
    },
  ];

  const byType = overview?.byType ?? [];
  const topConsumers = overview?.topConsumers ?? [];
  const atRisk = overview?.atRisk ?? [];
  const recentAlerts = overview?.recentAlerts ?? [];

  // The live signal: what the org-wide numbers on this page actually say.
  const effectiveBudgetCount = overview?.effectiveBudgetCount ?? 0;
  const overspent = atRisk.filter((r) => (r.consumedPercent ?? 0) >= 100);
  const guideTone = overspent.length > 0 ? 'error' : atRisk.length > 0 ? 'warning' : 'success';
  const guideSignal =
    effectiveBudgetCount === 0
      ? 'No approved budget is in period yet, so there is nothing to consume — approve a budget to start tracking.'
      : atRisk.length > 0
        ? `${atRisk.length} of ${effectiveBudgetCount} budget${effectiveBudgetCount === 1 ? '' : 's'} ${
            atRisk.length === 1 ? 'is' : 'are'
          } at 90%+ consumed${overspent.length > 0 ? `, ${overspent.length} already past 100%` : ''}.`
        : `Every budget is tracking comfortably — none of the ${effectiveBudgetCount} effective budget${
            effectiveBudgetCount === 1 ? '' : 's'
          } has passed 90% consumed.`;

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Budgets Dashboard</Title>
            <Text size="sm" c="dimmed">
              Advisory budget consumption across {overview?.effectiveBudgetCount ?? 0} effective budget
              {(overview?.effectiveBudgetCount ?? 0) === 1 ? '' : 's'}
            </Text>
          </div>
          <Group gap="sm">
            <Tooltip label={lastRefresh ? `Last refreshed: ${formatLastRefresh(lastRefresh)}` : 'Refresh data'}>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                loading={isRefreshing}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </Tooltip>
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/app/budgets/create')}>
              Create Budget
            </Button>
          </Group>
        </Group>

        <PageGuide
          storageKey="budgets-dashboard-admin"
          tone={guideTone}
          signal={guideSignal}
          action={{ label: 'See the forecast', onClick: () => navigate('/app/budgets/forecast') }}
        >
          The organization-wide position across every <strong>effective</strong> budget — approved and
          inside its period. Committed is spend that is promised but not yet paid; actual is money out
          the door. Every figure here comes from the budget ledger, so it moves as procurement and
          project spend lands.
        </PageGuide>

        {/* v2 admin quick links */}
        <AdminQuickLinks />

        {/* Stat Cards */}
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
          {statCards.map((card) => (
            <Card
              key={card.label}
              withBorder
              p="lg"
              style={card.onClick ? { cursor: 'pointer' } : undefined}
              onClick={card.onClick}
            >
              <Stack gap="xs">
                <Group justify="space-between">
                  <ThemeIcon variant="light" color={card.color} size="lg">
                    {card.icon}
                  </ThemeIcon>
                  <Badge variant="light" color={card.color}>
                    {card.label}
                  </Badge>
                </Group>
                <Text size="lg" fw={700}>
                  {card.value}
                </Text>
                {card.label === 'Unbudgeted' && (
                  <Text size="xs" c="dimmed">
                    {overview?.unbudgetedCount ?? 0} transaction{(overview?.unbudgetedCount ?? 0) === 1 ? '' : 's'}
                  </Text>
                )}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Consumption by type */}
        <Card withBorder p="lg">
          <Title order={4} mb="md">
            Consumption by Type
          </Title>
          {byType.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No effective budgets yet. Create and approve a budget to see consumption here.
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: byType.length > 3 ? 4 : byType.length }}>
              {byType.map((t) => (
                <Paper key={t.budgetTypeId ?? t.budgetTypeName} withBorder p="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Badge variant="light" color={t.budgetTypeColor || 'gray'}>
                        {t.budgetTypeName || `Type ${t.budgetTypeId}`}
                      </Badge>
                      <Text size="xs" c="dimmed">
                        {t.budgetCount ?? 0} budget{(t.budgetCount ?? 0) === 1 ? '' : 's'}
                      </Text>
                    </Group>
                    {/* Bar colour tracks health, not the type's brand colour — a 95%
                        consumed type must not read like a 10% one. */}
                    <Progress
                      value={Math.min(t.consumedPercent ?? 0, 100)}
                      gradientVariant={consumptionGradient(t.consumedPercent)}
                    />
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed">
                        {formatMoney((t.committed ?? 0) + (t.actual ?? 0))} consumed
                      </Text>
                      <Text size="xs" fw={600}>
                        {(t.consumedPercent ?? 0).toFixed(1)}%
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Available: {formatMoney(t.available)} of {formatMoney(t.allocated)}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          )}
        </Card>

        <Grid>
          {/* Top consumers */}
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Card withBorder p="lg" h="100%">
              <Group justify="space-between" mb="md">
                <Title order={4}>Top Consumers</Title>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/app/budgets/list')}
                >
                  View all
                </Button>
              </Group>
              {topConsumers.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No budget consumption recorded yet.
                </Text>
              ) : (
                <Stack gap="sm">
                  {topConsumers.map((row) => (
                    <UnstyledButton
                      key={row.budgetId}
                      onClick={() => navigate(`/app/budgets/view/${row.budgetId}`)}
                    >
                      <Paper withBorder p="sm">
                        <Stack gap={6}>
                          <Group justify="space-between" wrap="nowrap">
                            <Text size="sm" fw={500} lineClamp={1}>
                              {row.budgetName || row.budgetCode || `Budget #${row.budgetId}`}
                            </Text>
                            <BudgetHealthBadge
                              statusID={row.healthStatusId}
                              name={row.healthStatusName}
                              color={row.healthStatusColor}
                            />
                          </Group>
                          <Progress
                            value={Math.min(row.consumedPercent ?? 0, 100)}
                            gradientVariant={consumptionGradient(row.consumedPercent)}
                          />
                          <Group justify="space-between">
                            <Text size="xs" c="dimmed">
                              {formatMoney((row.committed ?? 0) + (row.actual ?? 0))} of{' '}
                              {formatMoney(row.allocated)}
                            </Text>
                            <Text size="xs" fw={600}>
                              {(row.consumedPercent ?? 0).toFixed(1)}% consumed
                            </Text>
                          </Group>
                          {(row.departmentName || row.projectTitle) && (
                            <Text size="xs" c="dimmed">
                              {row.departmentName || row.projectTitle}
                            </Text>
                          )}
                        </Stack>
                      </Paper>
                    </UnstyledButton>
                  ))}
                </Stack>
              )}
            </Card>
          </Grid.Col>

          {/* At risk + recent alerts */}
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Stack gap="lg" h="100%">
              <Card withBorder p="lg">
                <Group gap="xs" mb="md">
                  <ThemeIcon variant="light" color="orange" size="sm">
                    <IconAlertTriangle size={14} />
                  </ThemeIcon>
                  <Title order={4}>At Risk</Title>
                </Group>
                {atRisk.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    No budgets at 90%+ consumption.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {atRisk.map((row) => (
                      <UnstyledButton
                        key={row.budgetId}
                        onClick={() => navigate(`/app/budgets/view/${row.budgetId}`)}
                      >
                        <Paper withBorder p="xs">
                          <Group justify="space-between" wrap="nowrap">
                            <div style={{ minWidth: 0 }}>
                              <Text size="sm" lineClamp={1}>
                                {row.budgetName || `Budget #${row.budgetId}`}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {formatMoney(row.available)} available
                              </Text>
                            </div>
                            <Badge
                              variant="light"
                              color={consumptionColor(row.consumedPercent)}
                              size="sm"
                            >
                              {(row.consumedPercent ?? 0).toFixed(0)}%
                            </Badge>
                          </Group>
                        </Paper>
                      </UnstyledButton>
                    ))}
                  </Stack>
                )}
              </Card>

              <Card withBorder p="lg">
                <Group justify="space-between" mb="md">
                  <Group gap="xs">
                    <ThemeIcon variant="light" color="red" size="sm">
                      <IconBell size={14} />
                    </ThemeIcon>
                    <Title order={4}>Recent Alerts</Title>
                  </Group>
                  <Button
                    variant="subtle"
                    size="xs"
                    rightSection={<IconArrowRight size={14} />}
                    onClick={() => navigate('/app/budgets/alerts')}
                  >
                    All alerts
                  </Button>
                </Group>
                {recentAlerts.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    No unacknowledged alerts.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {recentAlerts.map((alert) => (
                      <UnstyledButton
                        key={alert.alertID}
                        onClick={() => navigate(`/app/budgets/view/${alert.budgetID}`)}
                      >
                        <Paper withBorder p="xs">
                          <Group justify="space-between" wrap="nowrap">
                            <div>
                              <Text size="sm" fw={500}>
                                {(alert.thresholdPercent ?? 0) >= 101
                                  ? 'Budget exceeded'
                                  : `${alert.thresholdPercent}% threshold crossed`}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {formatStampDateTime(alert.createdDateTime)}
                              </Text>
                            </div>
                            <Badge variant="light" color="red" size="sm">
                              {(alert.consumedPercent ?? 0).toFixed(0)}%
                            </Badge>
                          </Group>
                        </Paper>
                      </UnstyledButton>
                    ))}
                  </Stack>
                )}
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/* BudgetManager — "My Budgets"                                        */
/* ------------------------------------------------------------------ */

function MyBudgetCard({
  row,
  alertCount,
  onOpen,
}: {
  row: BudgetMyOverviewRow;
  alertCount: number;
  onOpen: () => void;
}) {
  const consumed = row.consumedPercent ?? 0;
  const released = row.releasedPercent ?? 0;

  return (
    <UnstyledButton onClick={onOpen} style={{ display: 'block', height: '100%' }}>
      <Card withBorder p="lg" h="100%">
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <div style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {row.budgetName || `Budget #${row.budgetId}`}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {[row.budgetCode, row.departmentName || row.projectTitle].filter(Boolean).join(' · ')}
              </Text>
            </div>
            <BudgetHealthBadge
              statusID={row.healthStatusId}
              name={row.healthStatusName}
              color={row.healthStatusColor}
            />
          </Group>

          <Group gap="xs">
            <BudgetStatusBadge status={{ statusID: row.statusId ?? 0, name: row.statusName }} />
            <BudgetTypeBadge typeID={row.budgetTypeId} name={row.budgetTypeName} color={row.budgetTypeColor} />
            {row.isDerivedEstimate && (
              <Tooltip label="Total is a live bottom-up estimate from the item list">
                <Badge variant="outline" color="gray" size="sm">
                  Estimate
                </Badge>
              </Tooltip>
            )}
            {alertCount > 0 && (
              <Badge variant="light" color="red" size="sm" leftSection={<IconBell size={10} />}>
                {alertCount} alert{alertCount === 1 ? '' : 's'}
              </Badge>
            )}
          </Group>

          {/* Consumed — bar colour tracks health so a 95% card can't read like a 10% one */}
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="xs" fw={600}>
                Spend
              </Text>
              <Text size="xs" fw={600} c={consumptionColor(consumed)}>
                {consumed.toFixed(1)}% consumed
              </Text>
            </Group>
            <Progress
              value={Math.min(consumed, 100)}
              gradientVariant={consumptionGradient(consumed)}
              size="md"
              radius="xl"
            />
            <Group justify="space-between" mt={2}>
              <Text size="xs" c="dimmed">
                {formatMoney((row.committed ?? 0) + (row.actual ?? 0))} of {formatMoney(row.allocated)}
              </Text>
              <Text size="xs" c="dimmed">
                Available: {formatMoney(row.available)}
              </Text>
            </Group>
          </div>

          {/* Released vs spent — a different measure (cash-flow), so a distinct colour */}
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="xs" fw={600}>
                Cash released
              </Text>
              <Text size="xs" fw={600} c="indigo">
                {released.toFixed(1)}% released
              </Text>
            </Group>
            <Progress value={Math.min(released, 100)} color="indigo" size="md" radius="xl" />
            <Group justify="space-between" mt={2}>
              <Text size="xs" c="dimmed">
                {formatMoney(row.releasedToDate)} released to date
              </Text>
              {(row.availableWithinReleased ?? 0) < 0 ? (
                <Tooltip label="Spend has outrun the release schedule — later releases catch up; the budget itself is not overspent">
                  <Badge variant="light" color="orange" size="xs">
                    Ahead of schedule by {formatMoney(Math.abs(row.availableWithinReleased ?? 0))}
                  </Badge>
                </Tooltip>
              ) : (
                <Text size="xs" c="dimmed">
                  Within released: {formatMoney(row.availableWithinReleased)}
                </Text>
              )}
            </Group>
          </div>
        </Stack>
      </Card>
    </UnstyledButton>
  );
}

function MyBudgetsDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboardService = useBudgetDashboardService();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: overview, isLoading } = useQuery({
    queryKey: ['budgets', 'dashboard', 'my-overview'],
    queryFn: () => dashboardService.getMyOverview(),
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['budgets', 'dashboard'] });
    setIsRefreshing(false);
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const budgets = overview?.budgets ?? [];
  const recentAlerts = overview?.recentAlerts ?? [];
  const myOpenChangeRequests = overview?.myOpenChangeRequests ?? [];

  const alertCountByBudget = new Map<number, number>();
  for (const alert of recentAlerts) {
    const id = alert.budgetID ?? 0;
    alertCountByBudget.set(id, (alertCountByBudget.get(id) ?? 0) + 1);
  }

  // The live signal: how many of MY budgets are running hot.
  const needsAttention = budgets.filter((b) => (b.consumedPercent ?? 0) >= 90);
  const overspent = needsAttention.filter((b) => (b.consumedPercent ?? 0) >= 100);
  const guideTone = overspent.length > 0 ? 'error' : needsAttention.length > 0 ? 'warning' : 'success';
  const guideSignal =
    budgets.length === 0
      ? 'You do not own or manage a budget yet, so there is nothing to track here.'
      : needsAttention.length > 0
        ? `${needsAttention.length} of your ${budgets.length} budget${budgets.length === 1 ? '' : 's'} ${
            needsAttention.length === 1 ? 'needs' : 'need'
          } attention — at 90%+ consumed${overspent.length > 0 ? `, ${overspent.length} already past 100%` : ''}.`
        : `Your budgets are all tracking within plan — none of the ${budgets.length} has passed 90% consumed.`;

  const totalCards = [
    {
      label: 'Allocated',
      value: formatMoney(overview?.totalAllocated),
      icon: <IconWallet size={22} />,
      color: 'blue',
    },
    {
      label: 'Committed',
      value: formatMoney(overview?.totalCommitted),
      icon: <IconLockDollar size={22} />,
      color: 'orange',
    },
    {
      label: 'Actual',
      value: formatMoney(overview?.totalActual),
      icon: <IconReceipt2 size={22} />,
      color: 'violet',
    },
    {
      label: 'Available',
      value: formatMoney(overview?.totalAvailable),
      icon: <IconCoins size={22} />,
      color: 'green',
    },
  ];

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>My Budgets</Title>
            <Text size="sm" c="dimmed">
              {budgets.length} budget{budgets.length === 1 ? '' : 's'} you own or manage
            </Text>
          </div>
          <Group gap="sm">
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconRefresh size={16} />}
              loading={isRefreshing}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/app/budgets/create')}>
              Create Budget
            </Button>
          </Group>
        </Group>

        <PageGuide
          storageKey="budgets-dashboard-mine"
          tone={guideTone}
          signal={guideSignal}
          action={{ label: 'See the forecast', onClick: () => navigate('/app/budgets/forecast') }}
        >
          The budgets you own or manage. <strong>Spend</strong> is how much of the budget is consumed
          (committed + actual); <strong>cash released</strong> is how much of it the release schedule has
          made available so far — outrunning the schedule is a timing matter, not an overspend.
        </PageGuide>

        {/* Totals */}
        <SimpleGrid cols={{ base: 2, lg: 4 }}>
          {totalCards.map((card) => (
            <Card key={card.label} withBorder p="lg">
              <Stack gap="xs">
                <Group justify="space-between">
                  <ThemeIcon variant="light" color={card.color} size="lg">
                    {card.icon}
                  </ThemeIcon>
                  <Badge variant="light" color={card.color}>
                    {card.label}
                  </Badge>
                </Group>
                <Text size="lg" fw={700}>
                  {card.value}
                </Text>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Budget cards */}
        {budgets.length === 0 ? (
          <Card withBorder p="lg">
            <Text c="dimmed" ta="center" py="xl">
              You don't own or manage any budgets yet. Create one to get started.
            </Text>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {budgets.map((row) => (
              <MyBudgetCard
                key={row.budgetId}
                row={row}
                alertCount={alertCountByBudget.get(row.budgetId ?? -1) ?? 0}
                onOpen={() => navigate(`/app/budgets/view/${row.budgetId}`)}
              />
            ))}
          </SimpleGrid>
        )}

        <Grid>
          {/* My open change requests */}
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Card withBorder p="lg" h="100%">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="orange" size="sm">
                  <IconGitPullRequest size={14} />
                </ThemeIcon>
                <Title order={4}>My Open Change Requests</Title>
              </Group>
              {myOpenChangeRequests.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="md">
                  No pending change requests.
                </Text>
              ) : (
                <Stack gap="xs">
                  {myOpenChangeRequests.map((cr) => {
                    const delta = (cr.requestedAmount ?? 0) - (cr.currentAmount ?? 0);
                    return (
                      <UnstyledButton
                        key={cr.changeRequestID}
                        onClick={() => navigate(`/app/budgets/view/${cr.budgetID}`)}
                      >
                        <Paper withBorder p="sm">
                          <Group justify="space-between" wrap="nowrap">
                            <div style={{ minWidth: 0 }}>
                              <Text size="sm" fw={500} lineClamp={1}>
                                {[cr.budgetCode, cr.budgetName].filter(Boolean).join(' · ') ||
                                  `Budget #${cr.budgetID}`}
                              </Text>
                              <Group gap={4} wrap="nowrap">
                                <Text size="xs" c="dimmed">
                                  {formatMoney(cr.currentAmount)} → {formatMoney(cr.requestedAmount)}
                                </Text>
                                {delta >= 0 ? (
                                  <IconArrowUpRight size={12} color="var(--mantine-color-green-6)" />
                                ) : (
                                  <IconArrowDownRight size={12} color="var(--mantine-color-red-6)" />
                                )}
                              </Group>
                              <Text size="xs" c="dimmed">
                                {formatStampDateTime(cr.createdDateTime)}
                              </Text>
                            </div>
                            <Badge variant="light" color={cr.statusColor || 'yellow'} size="sm">
                              {cr.statusName || 'Pending'}
                            </Badge>
                          </Group>
                        </Paper>
                      </UnstyledButton>
                    );
                  })}
                </Stack>
              )}
            </Card>
          </Grid.Col>

          {/* Recent alerts on my budgets */}
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Card withBorder p="lg" h="100%">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="red" size="sm">
                  <IconBell size={14} />
                </ThemeIcon>
                <Title order={4}>Recent Alerts</Title>
              </Group>
              {recentAlerts.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="md">
                  No unacknowledged alerts on your budgets.
                </Text>
              ) : (
                <Stack gap="xs">
                  {recentAlerts.map((alert) => (
                    <UnstyledButton
                      key={alert.alertID}
                      onClick={() => navigate(`/app/budgets/view/${alert.budgetID}`)}
                    >
                      <Paper withBorder p="xs">
                        <Group justify="space-between" wrap="nowrap">
                          <div>
                            <Text size="sm" fw={500}>
                              {(alert.thresholdPercent ?? 0) >= 101
                                ? 'Budget exceeded'
                                : `${alert.thresholdPercent}% threshold crossed`}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatStampDateTime(alert.createdDateTime)}
                            </Text>
                          </div>
                          <Badge variant="light" color="red" size="sm">
                            {(alert.consumedPercent ?? 0).toFixed(0)}%
                          </Badge>
                        </Group>
                      </Paper>
                    </UnstyledButton>
                  ))}
                </Stack>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );
}
